import { IconButton, Menu } from '@zillow/constellation-10';
import { IconMoreOutline, IconMoreVerticalOutline } from '@zillow/constellation-icons';
import ConversationActionsWrapper from './ConversationActionsWrapper';
import MenuContent from './MenuContent';
import ConversationActionIconButton from './ConversationActionIconButton';

export type Actions = {
  archiveConversation: () => void;
  markConversationAsUnread?: () => void;
  undoArchiveConversation: () => void;
  undoReportConversation: () => void;
  openReportConversationModal: () => void;
};

export type ConversationStatus = {
  isArchived: boolean;
  isSpam: boolean;
};

export type Breakpoint = 'xl' | 'xxl';

export type MenuPlacement = 'bottom-start' | 'bottom-end';

export type ConversationActionsProps = {
  actions: Actions;
  additionalActionButton?: React.ReactNode; // Option to render additional actions
  additionalMenuItem?: React.ReactNode; // Option to render additional menu items
  breakpoint?: Breakpoint;
  isBlocked: boolean;
  isImpersonator: boolean;
  menuPlacement?: MenuPlacement;
  shouldHideAdditionalMenuItemsOnDesktop?: boolean;
  shouldShowMarkUnread: boolean;
  status: ConversationStatus | null;
  moveToInboxLabel?: 'Move to inbox' | 'Move to recent';
};

export default function ConversationActions({
  actions,
  additionalActionButton,
  additionalMenuItem,
  breakpoint = 'xl',
  isBlocked,
  isImpersonator,
  menuPlacement = 'bottom-start',
  shouldHideAdditionalMenuItemsOnDesktop,
  shouldShowMarkUnread,
  status,
  moveToInboxLabel = 'Move to inbox',
}: ConversationActionsProps) {
  const { undoArchiveConversation, undoReportConversation } = actions;
  const { isArchived, isSpam } = status || {};
  const shouldShowMoveToInbox = !!isArchived || (!!isSpam && !isBlocked);
  const shouldShowSingleIconButton = !additionalMenuItem && shouldShowMoveToInbox;

  // If the conversation is archived or marked as spam, and there are no additional menu
  // items to display, a single icon button to move the conversation to inbox gets rendered
  // instead of rendering a menu
  return (
    <ConversationActionsWrapper>
      {additionalActionButton}

      {shouldShowSingleIconButton ? (
        <>
          {isArchived ? (
            <ConversationActionIconButton
              breakpoint={breakpoint}
              content="Unarchive"
              isDisabled={isImpersonator}
              onClick={() => undoArchiveConversation()}
              variant="unarchive"
            />
          ) : (
            <ConversationActionIconButton
              breakpoint={breakpoint}
              content={moveToInboxLabel}
              isDisabled={isImpersonator}
              onClick={() => undoReportConversation()}
              variant="moveToInbox"
            />
          )}
        </>
      ) : (
        <Menu
          content={
            <MenuContent
              actions={actions}
              additionalMenuItem={additionalMenuItem}
              isBlocked={isBlocked}
              isImpersonator={isImpersonator}
              moveToInboxLabel={moveToInboxLabel}
              shouldHideAdditionalMenuItemsOnDesktop={shouldHideAdditionalMenuItemsOnDesktop}
              shouldShowMarkUnread={shouldShowMarkUnread}
              shouldShowMoveToInbox={shouldShowMoveToInbox}
              status={status}
            />
          }
          dividers
          placement={menuPlacement}
          shouldAwaitInteractionResponse
          trigger={
            <span>
              <IconButton
                data-testid="more-options-vertical-button"
                css={{
                  ...(breakpoint === 'xl' && { xl: { display: 'none' } }),
                  ...(breakpoint === 'xxl' && { xxl: { display: 'none' } }),
                }}
                emphasis="bare"
                icon={<IconMoreVerticalOutline />}
                shape="square"
                size="md"
                title="More options"
                tone="brand"
              />

              <IconButton
                data-testid="more-options-button"
                css={{
                  display: 'none',
                  textAlign: 'center',
                  ...(breakpoint === 'xl' && { xl: { display: 'block' } }),
                  ...(breakpoint === 'xxl' && { xxl: { display: 'block' } }),
                }}
                emphasis="bare"
                icon={<IconMoreOutline />}
                shape="square"
                size="md"
                title="More options"
                tone="neutral"
              />
            </span>
          }
        />
      )}
    </ConversationActionsWrapper>
  );
}
